/**
 * useTariffSync - Hook for syncing tariff state to global TariffContext
 *
 * Determines the effective tariff based on:
 * 1. Manual rate mode (blended rates or TOU/tiered)
 * 2. Custom tariff data (approved tariffs or custom-built)
 *
 * Then syncs it to the global TariffContext for Financial Analysis.
 *
 * @module hooks/useTariffSync
 */

import { useEffect } from 'react';
import { debug } from '../utils/debug';

/**
 * @typedef {Object} TariffSyncOptions
 * @property {boolean} useManualRate - Whether manual rate mode is enabled
 * @property {string} blendedEnergyRate - Manual blended energy rate ($/kWh)
 * @property {string} blendedDemandRate - Manual blended demand rate ($/kW)
 * @property {Object|null} customTariffData - Custom tariff data object
 * @property {Object|null} vortxSettings - Vortx Flux solver settings (for TOU/tiered modes)
 * @property {Function} setSelectedTariff - Function from TariffContext to set effective tariff
 */

/**
 * Hook for syncing tariff state to global context
 *
 * Evaluates current tariff configuration and updates the TariffContext
 * so Financial Analysis and other consumers have access to the effective tariff.
 *
 * @param {TariffSyncOptions} options
 */
export function useTariffSync({
    useManualRate,
    blendedEnergyRate,
    blendedDemandRate,
    customTariffData,
    vortxSettings,
    setSelectedTariff,
}) {
    useEffect(() => {
        let effectiveTariff = null;

        if (useManualRate) {
            const energyRate = parseFloat(blendedEnergyRate);
            const demandRate = parseFloat(blendedDemandRate);
            const hasTou = vortxSettings?.rateMode === 'tou' && vortxSettings.touData?.periods;
            const hasTiered = vortxSettings?.rateMode === 'tiered' && vortxSettings.energyTiers?.length > 0;

            // Only consider it a valid tariff if inputs are valid
            if (hasTou || hasTiered) {
                // TOU or tiered modes are valid on their own
                effectiveTariff = {
                    id: 'manual_rate',
                    name: 'Custom Rate',
                    blended_annual_energy_rate: !isNaN(energyRate) && energyRate > 0 ? energyRate : null,
                    blended_annual_demand_rate: !isNaN(demandRate) ? demandRate : 0,
                };
            } else if (!isNaN(energyRate) && energyRate > 0) {
                // For simple manual rates, require valid energy rate
                effectiveTariff = {
                    id: 'manual_rate',
                    name: 'Custom Rate',
                    blended_annual_energy_rate: energyRate,
                    blended_annual_demand_rate: !isNaN(demandRate) && demandRate >= 0 ? demandRate : 0,
                };
            }
            // If none of the conditions are met, effectiveTariff remains null
        } else if (customTariffData) {
            // Use custom tariff data (from approved tariff selection or custom-built)
            effectiveTariff = customTariffData;
        }

        debug.log('[useTariffSync] Setting effective tariff:', {
            useManualRate,
            hasCustomTariff: !!customTariffData,
            effectiveTariff: effectiveTariff ? { id: effectiveTariff.id, name: effectiveTariff.name } : null,
        });

        setSelectedTariff(effectiveTariff);
    }, [
        useManualRate,
        blendedEnergyRate,
        blendedDemandRate,
        customTariffData,
        vortxSettings?.rateMode,
        vortxSettings?.touData,
        vortxSettings?.energyTiers,
        setSelectedTariff
    ]);
}

export default useTariffSync;
